import React, {useState, useEffect, useMemo, useRef} from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    LineController,
    Title,
    Tooltip,
    Legend,
    TimeScale,
    ChartOptions,
    ChartData,
    TooltipItem,
    Plugin
} from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import { Chart } from 'react-chartjs-2';
import 'chartjs-adapter-moment';
import moment from 'moment';
import { getTimeSeriesData } from '../../services/api.service';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, TimeScale, zoomPlugin);

interface TimeSeriesDataPoint {
    timestamp: string;
    value: number | null;
}

interface TimeSeriesData {
    data: TimeSeriesDataPoint[];
    metadata: {
        station_id: string;
        variable: string;
        start_date: string;
        end_date: string;
        count: number;
        total_count: number;
    };
}

interface WindSpeedDirectionChartProps {
    stationId: string;
    startDate: string;
    endDate: string;
    height?: number;
}

const WindSpeedDirectionChart: React.FC<WindSpeedDirectionChartProps> = ({
    stationId,
    startDate,
    endDate,
    height = 400
}) => {
    // State management
    const [windSpeedData, setWindSpeedData] = useState<TimeSeriesData | null>(null);
    const [windDirectionData, setWindDirectionData] = useState<TimeSeriesData | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

      const [chartKey, setChartKey] = useState<number>(0); // Add key to force chart re-render

    const chartRef = useRef<any>(null);

     // Reset zoom function that properly accesses the chart instance
    const resetZoom = () => {
        if (chartRef.current) {
            // Access the chart instance through the react-chartjs-2 wrapper
            const chartInstance = chartRef.current;
            if (chartInstance && chartInstance.resetZoom) {
                chartInstance.resetZoom();
            } else if (chartInstance && chartInstance.chart && chartInstance.chart.resetZoom) {
                chartInstance.chart.resetZoom();
            }
        }
    };


    // Data fetching effect
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);

            try {
                // Fetch both wind datasets in parallel
                const [windSpeedResponse, windDirectionResponse] = await Promise.allSettled([
                    getTimeSeriesData(stationId, 'ws', startDate, endDate), // Wind speed
                    getTimeSeriesData(stationId, 'wd', startDate, endDate)  // Wind direction
                ]);

                // Handle wind speed data response
                if (windSpeedResponse.status === 'fulfilled') {
                    setWindSpeedData(windSpeedResponse.value);
                } else {
                    console.error('Failed to fetch wind speed data:', windSpeedResponse.reason);
                }

                // Handle wind direction data response
                if (windDirectionResponse.status === 'fulfilled') {
                    setWindDirectionData(windDirectionResponse.value);
                } else {
                    console.error('Failed to fetch wind direction data:', windDirectionResponse.reason);
                }

            } catch (err) {
                setError('Failed to fetch wind data');
                console.error('Error fetching wind data:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [stationId, startDate, endDate]);

    // Helper function to convert wind direction degrees to compass direction
    const degreesToCompass = (degrees: number): string => {
        const directions = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
        const index = Math.round(degrees / 22.5) % 16;
        return directions[index];
    };

    // Chart data configuration
    const chartData: ChartData<'line'> = useMemo(() => {
        const datasets: any[] = [];

        // Wind speed dataset
        if (windSpeedData && windSpeedData.data && windSpeedData.data.length > 0) {
            datasets.push({
                label: 'Wind Speed (m/s)',
                data: windSpeedData.data
                    .filter(point => point.value !== null)
                    .map(point => ({
                        x: moment(point.timestamp).valueOf(),
                        y: point.value
                    })),
                borderColor: '#28a745',
                backgroundColor: 'rgba(40, 167, 69, 0.1)',
                borderWidth: 2,
                pointRadius: 2,
                pointHoverRadius: 4,
                fill: false,
                tension: 0.1,
                yAxisID: 'y'
            });
        }

        // Wind direction dataset
        if (windDirectionData && windDirectionData.data && windDirectionData.data.length > 0) {
            datasets.push({
                label: 'Wind Direction (°)',
                data: windDirectionData.data
                    .filter(point => point.value !== null)
                    .map(point => ({
                        x: moment(point.timestamp).valueOf(),
                        y: point.value
                    })),
                borderColor: '#007bff',
                backgroundColor: 'rgba(0, 123, 255, 0.1)',
                borderWidth: 2,
                pointRadius: 2,
                pointHoverRadius: 4,
                fill: false,
                tension: 0.1,
                yAxisID: 'y1'
            });
        }

        return { datasets };
    }, [windSpeedData, windDirectionData]);

    // Chart options configuration
    const chartOptions: ChartOptions<'line'> = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        interaction: {
            mode: 'nearest',
            axis: 'x',
            intersect: false
        },
        plugins: {
            title: {
                display: true,
                text: 'Wind Speed & Direction',
                font: {
                    size: 16
                }
            },
            legend: {
                display: true,
                position: 'top'
            },
            tooltip: {
                callbacks: {
                    title: (context) => {
                        if (context.length > 0) {
                            return moment(context[0].parsed.x).format('DD MMM YYYY HH:mm');
                        }
                        return '';
                    },
                    label: (context) => {
                        const value = context.parsed.y;
                        if (context.datasetIndex === 0) {
                            return `Wind Speed: ${value?.toFixed(2) || 0} m/s`;
                        } else {
                            const compass = degreesToCompass(value || 0);
                            return `Wind Direction: ${value?.toFixed(0) || 0}° (${compass})`;
                        }
                    }
                }
            },
             // Zoom plugin configuration
            zoom: {
                zoom: {
                    wheel: {
                        enabled: true,
                    },
                    pinch: {
                        enabled: true
                    },
                    mode: 'x',
                },
                pan: {
                    enabled: true,
                    mode: 'x',
                }
            }
        },
        scales: {
            x: {
                type: 'time',
                time: {
                    displayFormats: {
                        hour: 'HH:mm',
                        day: 'DD/MM',
                        week: 'DD/MM',
                        month: 'MM/YY'
                    },
                    tooltipFormat: 'DD MMM YYYY HH:mm'
                },
                title: {
                    display: true,
                    text: 'Time'
                }
            },
            y: {
                type: 'linear',
                display: true,
                position: 'left',
                title: {
                    display: true,
                    text: 'Wind Speed (m/s)',
                    color: '#28a745'
                },
                ticks: {
                    color: '#28a745',
                    callback: function (value: any) {
                        return `${value} m/s`;
                    }
                },
                grid: {
                    drawOnChartArea: true,
                },
                beginAtZero: true
            },
            y1: {
                type: 'linear',
                display: true,
                position: 'left',
                title: {
                    display: true,
                    text: 'Wind Direction (°)',
                    color: '#007bff'
                },
                ticks: {
                    color: '#007bff',
                    callback: function (value: any) {
                        const compass = degreesToCompass(value);
                        return `${value}° (${compass})`;
                    },
                    stepSize: 45 // Show major compass directions
                },
                grid: {
                    drawOnChartArea: false,
                },
                min: 0,
                max: 360
            }
        },
        parsing: false,
        normalized: true,
        spanGaps: false
    }), []);

    // Loading state
    if (loading) {
        return (
            <div className="card">
                <div className="card-body">
                    <div className="d-flex justify-content-center align-items-center" style={{ height: `${height}px` }}>
                        <div className="text-muted">
                            <i className="fa fa-spinner fa-spin fa-3x mb-3"></i>
                            <p>Loading wind speed and direction data...</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="card">
                <div className="card-body">
                    <div className="alert alert-danger">{error}</div>
                </div>
            </div>
        );
    }

    // No data state
    if (!windSpeedData?.data?.length && !windDirectionData?.data?.length) {
        return (
            <div className="card">
                <div className="card-body">
                    <div className="alert alert-warning">No wind data available for comparison</div>
                </div>
            </div>
        );
    }

    // Calculate summary statistics
    const validWindSpeedData = windSpeedData?.data?.filter(point => point.value !== null) || [];
    const validWindDirectionData = windDirectionData?.data?.filter(point => point.value !== null) || [];

    const avgWindSpeed = validWindSpeedData.length > 0 ?
        validWindSpeedData.reduce((sum, d) => sum + d.value!, 0) / validWindSpeedData.length : 0;

    const maxWindSpeed = validWindSpeedData.length > 0 ?
        Math.max(...validWindSpeedData.map(d => d.value!)) : 0;

    // Calculate predominant wind direction
    const predominantDirection = validWindDirectionData.length > 0 ?
        degreesToCompass(validWindDirectionData.reduce((sum, d) => sum + d.value!, 0) / validWindDirectionData.length) : 'N/A';

    // Main render
    return (
        <div className="card">
              <div className="card-header">
                <div className="d-flex justify-content-between align-items-center">
                    {/*<h5 className="card-title mb-0">{title}</h5>*/}
                    <div className="d-flex align-items-center gap-3">
                         {/* Reset zoom button */}
                        <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={resetZoom}
                            title="Reset Zoom"
                        >
                            <i className="fa fa-search-minus me-1"></i>
                            Reset Zoom
                        </button>

                    </div>
                </div>
            </div>
            <div className="card-body">
                <div style={{ height: `${height}px` }}>
                    <Chart type="line"  key={chartKey}
                        ref={chartRef} data={chartData} options={chartOptions} />
                </div>
                <div className="mt-2">
                    <small className="text-muted">
                        Wind speed and direction comparison over time
                        {validWindSpeedData.length > 0 && (
                            <span className="ms-3">
                                Wind Speed: {validWindSpeedData.length} points
                            </span>
                        )}
                        {validWindDirectionData.length > 0 && (
                            <span className="ms-3">
                                Wind Direction: {validWindDirectionData.length} points
                            </span>
                        )}
                        <span className="ms-3">
                            Avg Speed: {avgWindSpeed.toFixed(2)} m/s
                        </span>
                        <span className="ms-3">
                            Max Speed: {maxWindSpeed.toFixed(2)} m/s
                        </span>
                        <span className="ms-3">
                            Predominant Direction: {predominantDirection}
                        </span>
                    </small>
                </div>
            </div>
        </div>
    );
};

export default WindSpeedDirectionChart;